import * as React from "react";
import { Button, Text } from "@uikit";
import { navigate } from "gatsby";
import CartItem from "@components/book/cartItem";
import XE from "@components/common/xe";
import { IoCloseOutline } from "@react-icons/all-files/io5/IoCloseOutline";
import { useApp } from "@store/app";
import { useCheckout } from "@store/checkout";

interface IProps {
  onCheckout: () => void;
  onClose?: () => void;
}

const CartSummary = ({ onCheckout, onClose }: IProps) => {
  const display_currency = useApp(($) => $.display_currency);
  const cart = useCheckout(($) => $.cart);

  const cart_total = React.useMemo(() => {
    let total = 0;
    cart.forEach((item) => {
      let val = 0;

      // Green Fee
      val =
        (item?.golf?.green_fee?.cost + item?.golf?.green_fee?.margin) *
          item?.golf?.where_when_who?.number_of_golfers +
        Number(
          item?.golf?.where_when_who.number_of_golfers % 2
            ? item?.golf?.green_fee?.surcharge || 0
            : 0
        );

      // Caddies
      if (
        item?.golf?.caddies?.enabled &&
        item?.golf?.caddies?.payment_option !== null &&
        item?.golf?.caddies?.payment_option !== "ON_ARRIVAL"
      ) {
        val += item?.golf?.caddies?.total;
      }

      // Carts
      if (
        item?.golf?.carts?.enabled &&
        item?.golf?.carts?.payment_option !== null &&
        item?.golf?.carts?.payment_option !== "ON_ARRIVAL"
      ) {
        item?.golf?.carts?.types?.map(
          // eslint-disable-next-line array-callback-return
          (cart_type: any, idx: any) => {
            val += item?.golf?.carts?.types[idx]?.total;
          }
        );
      }

      if (item?.transfer?.vehicle?.vehicle?.id) {
        val +=
          item?.transfer?.vehicle?.total * item?.transfer?.vehicle?.quantity;
      }

      total += val;
    });
    return total;
  }, [cart]);

  let sortedCart = [...cart];
  sortedCart.sort((a, b) => {
    // sort by date then by golf_line?.tee_time?.start_time
    const dateA = a?.golf?.where_when_who?.date;
    const dateB = b?.golf?.where_when_who?.date;
    const timeA = a?.golf?.tee_time?.start_time;
    const timeB = b?.golf?.tee_time?.start_time;

    if (dateA < dateB) return -1;
    if (dateA > dateB) return 1;
    if (timeA < timeB) return -1;
    if (timeA > timeB) return 1;
    return 0;
  });

  if (cart?.length === 0) {
    return (
      <div>
        {" "}
        <div className="bg-white sm:hidden border-b border-grey3 px-5 py-3 sm:py-8 sm:px-10 flex justify-end">
          <button
            type="button"
            className="rounded-md inline-flex items-center justify-center text-gray-400"
            onClick={(e) => {
              onClose();
            }}
          >
            <span className="sr-only">Close menu</span>
            <IoCloseOutline className="h-6 w-6 text-grey7" aria-hidden="true" />
          </button>
        </div>
        <div className="flex flex-col pt-6 pb-11 px-10 w-full md:w-96 bg-white ">
          <div className="flex flex-col flex-1 items-center justify-center h-full">
            <img
              src={`${process.env.GATSBY_IMAGE_PREFIX}/website/empty_cart.png?quality=70`}
              alt="Cart Empty"
              className="h-[180px] w-auto"
            />
            <Text
              size={19}
              weight="semibold"
              leading={1.3}
              element="h1"
              color="black90"
              className="mb-3 text-center"
            >
              Your cart is empty
            </Text>
            <Text
              size={15}
              leading={1.5}
              element="p"
              color="black90"
              className="mb-6 text-center"
            >
              Looks like you haven’t added anything to your cart yet.
            </Text>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div>
      <div className="bg-white sm:hidden border-b border-grey3 px-5 py-4 sm:py-8 sm:px-10 flex justify-center relative">
        <div>
          <Text size={17} weight="semibold" leading={1.3} element="h1">
            Your Cart ({cart?.length} item
            {cart?.length > 1 && "s"})
          </Text>
        </div>
        <button
          type="button"
          className="absolute right-4 inline-flex items-center justify-center"
          onClick={(e) => {
            onClose();
          }}
        >
          <span className="sr-only">Close menu</span>
          <IoCloseOutline className="h-6 w-6 text-grey7" aria-hidden="true" />
        </button>
      </div>
      <div className="flex flex-col py-5 pb-8 sm:py-8 w-full md:w-[500px] sm:border border-grey3 bg-white sm:rounded-lg ">
        <div>
          <div className="hidden sm:block  px-5 sm:px-10">
            <Text
              size={21}
              weight="semibold"
              leading={1.2}
              element="h1"
              className="mb-6"
            >
              Your Cart ({cart?.length} item
              {cart?.length > 1 && "s"})
            </Text>
          </div>
          <div className="flex flex-col gap-5 max-h-[55dvh] overflow-auto  px-5 sm:px-10">
            {sortedCart.map((item, index) => {
              return (
                <CartItem
                  key={index}
                  summaryMode={true}
                  display_currency={display_currency.code}
                  showHeading={true}
                  note={item.note}
                  golf_line={item.golf}
                  transfer_line={item.transfer}
                  showTotal={true}
                  onDelete={() => {
                    //setSelectedItem(item);
                    // dispatch(removeFromCart(item.id));
                  }}
                  showBreakdown={true}
                />
              );
            })}
          </div>
          <div className="pt-[10px] flex gap-4 flex-col  px-5 sm:px-10">
            <div className="flex justify-between">
              <div>
                <Text
                  size={15}
                  sizeTablet={17}
                  sizeDesktop={17}
                  weight="semibold"
                  leading={1.3}
                  color="black"
                  className="mb-0"
                >
                  Cart Total
                </Text>
                <Text
                  size={11}
                  sizeTablet={13}
                  sizeDesktop={13}
                  leading={1.5}
                  color="black80"
                >
                  {cart.length} item
                  {cart.length > 1 && "s"}
                </Text>
              </div>
              <div>
                <Text
                  size={15}
                  sizeTablet={17}
                  sizeDesktop={17}
                  weight="semibold"
                  leading={1.3}
                  color="black"
                  className="mb-0"
                >
                  <XE
                    base_currency={
                      cart.length > 0 && cart[0]?.golf?.supplier?.currency
                    }
                    currency={display_currency.code}
                    value={cart_total}
                  />
                </Text>
                {display_currency.code !==
                  (cart.length > 0 && cart[0]?.golf?.supplier?.currency) && (
                  <Text
                    size={11}
                    sizeTablet={13}
                    sizeDesktop={13}
                    leading={1.5}
                    color="black60"
                    className="text-right"
                  >
                    <XE
                      base_currency={
                        cart.length > 0 && cart[0]?.golf?.supplier?.currency
                      }
                      currency={
                        cart.length > 0 && cart[0]?.golf?.supplier?.currency
                      }
                      value={cart_total}
                    />
                  </Text>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={async () => {
                  onClose();
                  navigate("/cart");
                }}
                size="large"
                color="green"
                invert
                width="full"
                text="View Cart"
                className="!m-0"
              />
              <Button
                onClick={onCheckout}
                size="large"
                color="green"
                width="full"
                text="Checkout now"
                className="!m-0"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartSummary;
